import { z } from 'zod';
import { formatWeekday } from '@/lib/dates';

// Backend identifiers are server-issued (UUIDs) and the
// values are always picked from a server-controlled dropdown. We only need
// to ensure a non-empty string — a strict format regex would false-reject
// real IDs whose alphabet doesn't match exactly. The backend remains the
// authority on identifier validity.
const Id = z.string().min(1, 'Required');

/** A business calendar date, "YYYY-MM-DD": show with formatDate, never via `new Date()`. */
const BusinessDate = z.string();
/** An instant (ISO; naive = UTC): show with formatDateTime, on the EAT clock. */
const Timestamp = z.string();

export const ContractStatus = z.enum([
  'draft',
  'pending_payment',
  'active',
  'completed',
  'defaulted',
  'cancelled',
]);
export type ContractStatusType = z.infer<typeof ContractStatus>;

export const PaymentScheduleItem = z.object({
  id: Id,
  dueDate: BusinessDate,
  amount: z.number(),
  paidAmount: z.number(),
  status: z.enum(['pending', 'paid', 'partial', 'overdue', 'waived']),
  paidAt: Timestamp.nullable(),
});
export type PaymentScheduleItemModel = z.infer<typeof PaymentScheduleItem>;

export const ContractReleaseStatus = z.enum([
  'released',
  'pending',
  'not_applicable',
]);
export type ContractReleaseStatusType = z.infer<typeof ContractReleaseStatus>;

export const ContractZaadStatus = z.enum([
  'notified',
  'pending',
  'not_applicable',
]);
export type ContractZaadStatusType = z.infer<typeof ContractZaadStatus>;

/** Read-only settlement position of a cancelled contract. */
export const ContractCancellation = z.object({
  releaseStatus: ContractReleaseStatus,
  zaadStatus: ContractZaadStatus,
  zaadNotifiedAt: z.string().nullable(),
  upfrontCollected: z.number().nullable(),
  installmentsCollected: z.number().nullable(),
  writtenOff: z.number().nullable(),
});
export type ContractCancellationModel = z.infer<typeof ContractCancellation>;

export const Contract = z.object({
  id: Id,
  contractNumber: z.string(),
  customerId: Id,
  customerName: z.string(),
  customerPhone: z.string().nullable(),
  /**
   * The customer's platform user id — the `sub` their app token carries.
   * Null until they first use the app; documents uploaded against this
   * contract are stamped with it so the app can read them back.
   */
  customerUserId: z.string().nullable(),
  deviceId: Id,
  deviceName: z.string(),
  deviceBrand: z.string().optional(),
  deviceModel: z.string().optional(),
  financingPlanId: Id,
  planName: z.string(),
  devicePrice: z.number(),
  downPayment: z.number(),
  financedAmount: z.number(),
  markupAmount: z.number(),
  totalAmount: z.number(),
  monthlyPayment: z.number(),
  tenureMonths: z.number(),
  creditLimit: z.number().nullable().optional(),
  upfrontExcess: z.number().nullable().optional(),
  financedPrincipal: z.number().nullable().optional(),
  // Set when loan-service has accepted the loan; null until activation
  // (or whenever activation has been retried but still no loan exists).
  loanId: z.string().nullable().optional(),
  status: ContractStatus,
  startDate: BusinessDate,
  endDate: BusinessDate,
  signedAt: Timestamp.nullable().optional(),
  /**
   * Installments paid so far, or null when the backend could not derive it.
   * Never flattened to 0 — "nothing paid" is a claim about the customer, and
   * an absent field is not entitled to make it.
   */
  totalPaid: z.number().nullable(),
  /**
   * What the contract still owes, or null when the inputs cannot produce a
   * balance. The backend sends null deliberately, distinct from 0.0 on the
   * wire, and logs the reason: a fabricated zero reads as a settled contract.
   * This field being absent is what printed "Remaining $0.00" on a contract
   * owing $120.
   */
  remainingBalance: z.number().nullable(),
  nextPaymentDate: BusinessDate.nullable(),
  overdueAmount: z.number(),
  installmentFrequency: z.string().nullable().optional(),
  paymentDayOfWeek: z.number().nullable().optional(),
  paymentDayOfMonth: z.number().nullable().optional(),
  installmentPlan: z
    .array(
      z.object({
        n: z.number(),
        due: BusinessDate,
        amt: z.number(),
        status: z.string().optional(),
        paidAmount: z.number().optional(),
      })
    )
    .nullable()
    .optional(),
  paymentSchedule: z.array(PaymentScheduleItem).optional(),
  branchId: Id.nullable(),
  branchName: z.string().nullable(),
  createdBy: z.string(),
  createdAt: Timestamp,
  updatedAt: Timestamp.nullable(),
  // Present only once the contract is cancelled; older responses omit it.
  cancellation: ContractCancellation.nullable().optional(),
  // Write-off moves the outstanding balance to bad debt; status stays defaulted.
  writtenOffAt: Timestamp.nullable().optional(),
  writtenOffByName: z.string().nullable().optional(),
  writeOffReason: z.string().nullable().optional(),
  writeOffAmount: z.number().nullable().optional(),
});
export type ContractModel = z.infer<typeof Contract>;

/** The number printed on the agreement and used in Odoo, e.g. "DF-019E89D8". */
export function contractDisplayNumber(contractId: string): string {
  return `DF-${contractId.slice(0, 8).toUpperCase()}`;
}

/**
 * The rhythm the stored schedule actually runs on: installment *n* falls on
 * `startDate + n × periodDays` in plain calendar days, so every day is a
 * collection day and a 30-day month leaves monthly plans on no fixed
 * day-of-month. Null when the contract carries no schedule to read.
 */
export function paymentRhythm(
  contract: Pick<ContractModel, 'installmentFrequency' | 'installmentPlan'>
): string | null {
  if (contract.installmentFrequency === 'daily') return 'Every day';
  if (contract.installmentFrequency === 'monthly') return 'Every 30 days';
  if (contract.installmentFrequency !== 'weekly') return null;

  const firstDue = contract.installmentPlan?.[0]?.due;
  if (!firstDue) return null;
  const weekday = formatWeekday(firstDue.split('T')[0], '');
  if (!weekday) return null;
  return `Every ${weekday}`;
}

/**
 * How far through the installment plan the customer is, or null when that
 * cannot be said.
 *
 * Null rather than 0 whenever an input is missing: a bar sitting at zero is a
 * statement that nothing has been repaid, which is the same fabrication as a
 * $0.00 balance on a contract that owes $120. A zero returned here means a real
 * zero — money is expected and none of it has arrived.
 */
export function installmentProgressPercent(
  totalPaid: number | null | undefined,
  totalAmount: number | null | undefined
): number | null {
  if (totalPaid == null || totalAmount == null || totalAmount <= 0) return null;
  return Math.min(100, Math.max(0, Math.round((totalPaid / totalAmount) * 100)));
}

export const EscalationLevel = z.enum([
  'none',
  'warning',
  'partial_lock',
  'full_lock',
  'defaulted',
]);
export type EscalationLevelType = z.infer<typeof EscalationLevel>;

export const ContractListItem = z.object({
  id: Id,
  contractNumber: z.string(),
  customerName: z.string(),
  customerPhone: z.string().nullable().optional(),
  deviceName: z.string(),
  deviceBrand: z.string().optional(),
  deviceModel: z.string().optional(),
  totalAmount: z.number(),
  monthlyPayment: z.number(),
  status: ContractStatus,
  startDate: BusinessDate,
  overdueAmount: z.number(),
  nextPaymentDate: BusinessDate.nullable(),
  nextPaymentAmount: z.number().optional().default(0),
  installmentFrequency: z.string().nullable().optional(),
  // Live escalation snapshot from backend (computed via lock-engine thresholds)
  escalationLevel: EscalationLevel.optional().default('none'),
  daysOverdue: z.number().optional().default(0),
  writtenOffAt: Timestamp.nullable().optional(),
});
export type ContractListItemModel = z.infer<typeof ContractListItem>;

export const ContractActivityCategory = z.enum([
  'lifecycle',
  'payment',
  'collection_attempt',
]);
export type ContractActivityCategoryType = z.infer<
  typeof ContractActivityCategory
>;

export const ContractActivityStatus = z.enum(['success', 'pending', 'failed']);
export type ContractActivityStatusType = z.infer<
  typeof ContractActivityStatus
>;

export const ContractActivityEvent = z.object({
  id: z.string(),
  occurredAt: Timestamp,
  category: ContractActivityCategory,
  status: ContractActivityStatus,
  amount: z.number().nullable().optional(),
  installmentNumber: z.number().nullable().optional(),
  attemptStatus: z.string().nullable().optional(),
  reasonCode: z.string().nullable().optional(),
  errorMessage: z.string().nullable().optional(),
  attemptCount: z.number().nullable().optional(),
  maxAttempts: z.number().nullable().optional(),
  nextAttemptAt: Timestamp.nullable().optional(),
  dueDate: Timestamp.nullable().optional(),
  paymentType: z.string().nullable().optional(),
  paymentMethod: z.string().nullable().optional(),
  providerTransactionId: z.string().nullable().optional(),
  /** Set on a `payment_reversed` lifecycle row: the payment it took back. */
  paymentId: z.string().nullable().optional(),
  toState: z.string().nullable().optional(),
  message: z.string().nullable().optional(),
  actorName: z.string().nullable().optional(),
  data: z.record(z.string(), z.unknown()).nullable().optional(),
});
export type ContractActivityEventModel = z.infer<typeof ContractActivityEvent>;

/** A refusal the customer must resolve; retrying alone will never clear it. */
const CUSTOMER_ACTION_REASONS = new Set([
  'WALLETNOTACTIVE',
  'WALLET_LOCKED',
  'MANDATENOTFOUND',
  'MANDATEEXPIRED',
  'MANDATEREVOKED',
]);

export type CollectionFailureKind = 'customer_action' | 'retryable' | 'ops';

export function collectionFailureKind(
  event: ContractActivityEventModel
): CollectionFailureKind {
  const reason = event.reasonCode ?? '';
  if (CUSTOMER_ACTION_REASONS.has(reason)) return 'customer_action';
  if (reason === 'BLOCKED' || reason === 'KICKOFF_FAILED') return 'ops';
  return 'retryable';
}

/** Failed attempts with no retry left — the queue an agent has to work. */
export function unresolvedAttempts(
  events: ContractActivityEventModel[] | undefined
): ContractActivityEventModel[] {
  return (events ?? []).filter(
    (e) => e.category === 'collection_attempt' && e.status === 'failed'
  );
}

/** The contract detail page's tabs, in display order. */
export const CONTRACT_TABS = [
  'overview',
  'payments',
  'loan',
  'documents',
  'contact',
  'activity',
] as const;
export type ContractTab = (typeof CONTRACT_TABS)[number];

/**
 * `?highlight=` is written by several places (the Payments list, the
 * dashboard's recent transactions, the receipt drawer) and none of them also
 * set `tab=payments`. Radix's `TabsContent` unmounts inactive panels, so a
 * highlighted row that lands on any other tab is never in the DOM to scroll
 * to. Deriving the default here — rather than requiring every caller to
 * remember a second search param — makes the deep link work regardless of
 * where it was built.
 */
export function resolveContractTab(search: {
  tab?: ContractTab;
  highlight?: string;
}): ContractTab {
  return search.tab ?? (search.highlight ? 'payments' : 'overview');
}

/**
 * Query key for a contract's rendered agreement PDF.
 *
 * Lives beside the model rather than beside the component so the contract
 * page can drop the cached document after activation without importing UI.
 */
export const agreementPdfQueryKey = (contractId: string) =>
  ['contract-agreement-pdf', contractId] as const;

export function activityTitle(event: ContractActivityEventModel): string {
  if (event.category === 'lifecycle') {
    if (event.toState === 'reverted_to_active') return 'Reverted to active';
    if (event.toState === 'written_off') return 'Written off';
    if (event.toState === 'payment_reversed') return 'Payment reversed';
    if (event.toState === 'defaulted') return 'Defaulted';
    return event.toState === 'registered'
      ? 'Registered with ZaadCredit'
      : 'Contract created';
  }
  if (event.category === 'payment') {
    const kind = event.paymentType === 'down_payment' ? 'Down payment' : 'Payment';
    return event.status === 'failed' ? `${kind} failed` : kind;
  }
  const n = event.installmentNumber;
  const label = n ? `Installment ${n}` : 'Installment';
  if (event.status === 'success') return `${label} collected`;
  if (event.status === 'pending') return `${label} collection in progress`;
  return `${label} collection failed`;
}
